import type { MarketsApi } from "@/v2/api/metadata/markets";
import type { OutcomesApi } from "@/v2/api/metadata/outcomes";
import type { DialogConfig } from "@/v2/framework/dialog/types";
import { outcomeGamesStatusOptions, outcomeStatusOptions, outcomeTypeOptions } from "../../shared";
import { buildAddOutcomeSupplementalNames } from "./outcome-fields";

export function createAddDialog(options: {
  outcomesApi: OutcomesApi;
  marketsApi: MarketsApi;
}): DialogConfig {
  const { outcomesApi, marketsApi } = options;

  return {
    id: "add-outcome",
    title: "Add outcome",
    trigger: { type: "fab" },
    layout: { type: "popover" },
    structure: {
      type: "stepper",
      steps: [
        {
          id: "main",
          children: [
            {
              type: "group",
              name: "main",
              label: "Main",
              children: [
                {
                  type: "text-field",
                  name: "name",
                  label: "Name",
                  required: true,
                  placeholder: "Enter a name",
                },
                {
                  type: "text-field",
                  name: "eventMarketOutcomeName",
                  label: "Event market outcome name",
                  required: true,
                  placeholder: "Enter event market outcome name",
                },
                {
                  type: "select-field",
                  name: "marketId",
                  label: "Market",
                  required: true,
                  placeholder: "Select market",
                  options: async () => {
                    const markets = await marketsApi.getFilters({ page: 1, size: -1 });
                    return markets.map((market) => ({
                      value: market.id as string | number,
                      title: String(market.computedName ?? market.name ?? ""),
                    }));
                  },
                },
                {
                  type: "select-field",
                  name: "type",
                  label: "Outcome type",
                  required: true,
                  options: outcomeTypeOptions,
                  defaultValue: 1,
                },
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: outcomeStatusOptions,
                  defaultValue: 1,
                },
                { type: "text-field", name: "info", label: "Info", placeholder: "Enter info" },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  placeholder: "Enter position",
                  inputType: "number",
                },
                {
                  type: "component",
                  component: () => import("./OutcomeSupplementalNamesFields.vue"),
                  props: { mode: "add" },
                },
                {
                  type: "component",
                  component: () => import("./OutcomeSpecifiersFields.vue"),
                  props: { mode: "add", marketsApi },
                },
              ],
            },
            {
              type: "group",
              name: "prematchGame",
              label: "Pre-match",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: outcomeGamesStatusOptions,
                },
              ],
            },
            {
              type: "group",
              name: "liveGame",
              label: "Live",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: outcomeGamesStatusOptions,
                },
              ],
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        const main = (data.main ?? {}) as Record<string, unknown>;
        const prematchGame = (data.prematchGame ?? {}) as Record<string, unknown>;
        const liveGame = (data.liveGame ?? {}) as Record<string, unknown>;
        const specifiers = Array.isArray(data.specifiers) ? data.specifiers : [];
        const competitors = Array.isArray(data.competitors) ? data.competitors : [];

        const model: Record<string, unknown> = {
          name: main.name,
          eventMarketOutcomeName: main.eventMarketOutcomeName,
          marketId: main.marketId,
          type: main.type,
          status: main.status,
          info: main.info === "" ? null : main.info,
          position: main.position === "" || main.position == null ? null : Number(main.position),
          supplementalNames: buildAddOutcomeSupplementalNames(data),
          specifiers: specifiers.map((row) => {
            const specifier = row as Record<string, unknown>;
            const split = (value: unknown) =>
              String(value ?? "")
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean);
            return {
              outcomeSpecifierType: specifier.outcomeSpecifierType,
              ordinal: specifier.ordinal === "" || specifier.ordinal == null ? null : Number(specifier.ordinal),
              validations: split(specifier.validations),
              defaultValues: split(specifier.defaultValues),
            };
          }),
          competitors: competitors.map((row) => {
            const competitor = row as Record<string, unknown>;
            return {
              competitorType: competitor.competitorType,
              ordinal:
                competitor.ordinal === "" || competitor.ordinal == null
                  ? null
                  : Number(competitor.ordinal),
            };
          }),
          prematchGame: { status: prematchGame.status },
          liveGame: { status: liveGame.status },
        };

        return outcomesApi.create(model);
      },
    },
  };
}
